import { Box, CircularProgress, Typography } from "@mui/material"
import { AttachFileOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { useGetSalesQuoteEmailPreview } from "../../hooks/useGetSalesQuoteEmailPreview"

import { QuoteEmailAttachmentCard } from "./QuoteEmailAttachmentCard"

import { EmailPreviewDialog, emailPreviewStyles } from "@/components/common/Modals/EmailPreviewDialog"
import { formatEmailTimestamp } from "@/utils/date"

const attachmentCountLabel = (count: number): string => (count === 1 ? "One attachment" : `${count} attachments`)

interface QuoteEmailPreviewDialogProps {
  quoteGroupId: number
  emailNote: string
  isSending: boolean
  onBack: () => void
  onSend: () => Promise<void> | void
}

// The body is the server-composed email HTML, so the preview and the sent email
// can never drift. Paragraph styling is applied here rather than in the markup.
const emailBodyStyles = {
  ...emailPreviewStyles.body,
  "& p": { ...emailPreviewStyles.body, marginBottom: "16px" },
  "& p:last-child": { marginBottom: 0 },
  "& strong": emailPreviewStyles.bodyBold,
}

export const QuoteEmailPreviewDialog: FC<QuoteEmailPreviewDialogProps> = ({
  quoteGroupId,
  emailNote,
  isSending,
  onBack,
  onSend,
}) => {
  const { preview, isFetching, error } = useGetSalesQuoteEmailPreview(quoteGroupId, emailNote)
  const isLoading = isFetching && !preview
  const attachments = preview?.attachments ?? []

  return (
    <EmailPreviewDialog
      isSending={isSending}
      onBack={onBack}
      onSend={onSend}
      sendDisabled={isLoading || !!error || preview?.canSend === false}
      testid='sales.email-preview.dialog'
    >
      {isLoading ? (
        <Box className='flex justify-center py-20' data-testid='sales.email-preview.loading'>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box className='flex flex-col gap-6 px-12 py-10'>
          <Box className='flex flex-col gap-2 border-b border-gray-400 pb-4 text-gray-900'>
            <Typography sx={emailPreviewStyles.subject}>{preview?.subject}</Typography>
            <Box className='flex items-center justify-between'>
              <Typography sx={emailPreviewStyles.sender}>Akrochem&lt;messaging-noreply@akrochem.com&gt;</Typography>
              <Box className='flex items-center gap-2'>
                {preview?.submittedOn && (
                  <Typography sx={emailPreviewStyles.sender} data-testid='sales.email-preview.submitted-on'>
                    {formatEmailTimestamp(preview.submittedOn)}
                  </Typography>
                )}
                {attachments.length > 0 && <AttachFileOutlined fontSize='small' />}
              </Box>
            </Box>
          </Box>

          <Box
            className='text-gray-900'
            sx={emailBodyStyles}
            data-testid='sales.email-preview.body'
            dangerouslySetInnerHTML={{ __html: preview?.bodyHtml ?? "" }}
          />

          {attachments.length > 0 && (
            <Box className='flex flex-col gap-3 border-t border-gray-400 pt-4'>
              <Box className='flex items-center gap-2'>
                <AttachFileOutlined fontSize='small' />
                <Typography sx={emailPreviewStyles.attachment}>{attachmentCountLabel(attachments.length)}</Typography>
              </Box>
              <Box className='flex flex-wrap gap-3'>
                {attachments.map((attachment) => (
                  <QuoteEmailAttachmentCard
                    key={attachment.filename}
                    filename={attachment.filename}
                    signedUrl={attachment.signedUrl}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </EmailPreviewDialog>
  )
}
